import React, { useState, useEffect, useContext } from 'react';
import { formatTodayHours } from '../utils/formatTodayHours';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaUtensils, FaArrowLeft, FaSpinner } from 'react-icons/fa';
import { Container, Row, Col } from 'react-bootstrap';
import '../styles/RestaurantDetail.css';
import ItemModal from '../components/ItemModal';
import { StoreContext } from '../context/StoreContext';
import { isRestaurantOpen } from '../utils/isRestaurantOpen';
import { fetchRestaurantById } from '../api/apiService';
import { formatPrice } from '../utils/formatPrice';
import { useClosingStatus } from '../utils/useClosingStatus';

const MENU_CATEGORIES = [
  { label: 'All', value: 'all' },
  { label: 'Veg', value: 'veg' },
  { label: 'Non-Veg', value: 'nonveg' },
  { label: 'Tiffins', value: 'tiffins' },
];

const RestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const { addItemToCart, updateItemQuantity, cart } = useContext(StoreContext);

  useEffect(() => {
    const loadRestaurantDetails = async () => {
      if (!id) {
        setError("Restaurant ID is missing.");
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await fetchRestaurantById(id);
        setRestaurant(response.data);
      } catch (err) {
        setError("Failed to load restaurant details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadRestaurantDetails();
  }, [id]);

   const { isClosingSoon, minutesLeft, nextDayDeliveryTime } = useClosingStatus(
  restaurant?.operatingHours || restaurant?.timings,
  restaurant?.location?.country
);

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleItemAdd = (itemPayload) => {
    if (itemPayload.hasSpicyLevels && !itemPayload.hasOwnProperty('spicyLevel')) {
      itemPayload.spicyLevel = 'medium';
    }

    addItemToCart({
      ...itemPayload,
      item_id: itemPayload._id,
      vendor_id: restaurant._id,
      vendor_type: 'restaurant',
    });

    setShowModal(false);
  };

  const formatTime = (time) => {
    if (!time) return '';
    let [timePart, amPm] = time.split(' ');
    if (!amPm) amPm = 'AM';
    const [hours, mins] = timePart.split(':');
    const paddedMins = mins ? mins.padEnd(2, '0') : '00';
    return `${hours}:${paddedMins} ${amPm.toUpperCase()}`;
  };

  if (loading) {
    return <div className="detail-container loading-state"><FaSpinner className="spinner" /> Loading restaurant...</div>;
  }

  if (error || !restaurant) {
    return (
      <div className="detail-container error-state">
        <p className="error-message">{error || "Restaurant not found."}</p>
        <Link to="/" className="back-link"><FaArrowLeft /> Back to Home</Link>
      </div>
    );
  }

  const bannerImg = restaurant.image || 'https://via.placeholder.com/600x200?text=Restaurant';
  const locationStr = restaurant.location ? [restaurant.location.street, restaurant.location.city].filter(Boolean).join(', ') : '';
  const restaurantItems = Array.isArray(restaurant.items) ? restaurant.items : [];

  const filteredItems = restaurantItems.filter(item => {
    if (item.isAvailable !== true) return false;
    if (selectedCategory === 'all') return true;
    
    const itemType = (item.type || '').toLowerCase().replace(/[\s-]/g, '');
    const selectedCat = selectedCategory.toLowerCase().replace(/[\s-]/g, '');
    return itemType === selectedCat;
  });

  const open = isRestaurantOpen(
  restaurant?.operatingHours || restaurant?.timings,
  restaurant?.location?.country
);
  const currency = restaurant?.currency;
  const currencySymbol = currency === 'CAD' ? 'CAD' : (currency === 'USD' ? '$' : '₹');

  return (
    <motion.div
      className="restaurant-detail-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="detail-header">
        <button className="back-button" onClick={() => navigate(-1)}><FaArrowLeft /></button>
        <img src={bannerImg} alt={restaurant.name} className="detail-banner-img" />
        <div className="detail-overlay">
          <h1 className="detail-name">{restaurant.name}</h1>
          <p className="detail-info"><FaUtensils /> {restaurant.cuisine} • <FaMapMarkerAlt /> {locationStr}</p>
        </div>
      </div>

      {/* ✅ MOVED HERE: Closing Banner */}
      {isClosingSoon && (
        <div className="closing-banner-wrapper">
          <motion.div 
            className="closing-alert-banner"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="closing-alert-content">
              <span className="closing-icon">⚠️</span>
              <div className="closing-text">
                <strong>Closing in {minutesLeft} minutes!</strong>
                <span>Orders placed now will be delivered tomorrow by <b>{nextDayDeliveryTime}</b>.</span>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <Container style={{ maxWidth: 1100, padding: 0 }}>
        <div className="detail-info-bar">
          <div className="detail-info-box timings">
            <span className="detail-info-label">Timings</span>
            <span className="detail-info-value">
              {formatTodayHours(restaurant?.operatingHours || restaurant?.timings)}
            </span>
          </div>

          <span className={`status-badge ${open ? 'open' : 'closed'}`}>
            {open ? 'Opened' : 'Closed'}
          </span>
          <div className="detail-info-box delivery">
            <span className="detail-info-label">Delivery Time</span>
            <span className="detail-info-value">
              {restaurant.deliveryTime || 'N/A'}
            </span>
          </div>
        </div>
        
        <div className="detail-content">
          <h2 className="menu-heading">Menu</h2>
          <div className="menu-category-tabs">
            {MENU_CATEGORIES.map(cat => (
              <button
                key={cat.value}
                className={`menu-category-tab${selectedCategory === cat.value ? ' active' : ''}`}
                onClick={() => setSelectedCategory(cat.value)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {filteredItems && filteredItems.length > 0 ? (
            <Row className="g-4">
              {filteredItems.map((item) => {
                const itemId = item._id;
                const cartItem = cart?.items?.find(ci => ci.item_id?.toString() === itemId?.toString());
                const qty = cartItem ? cartItem.quantity : 0;
                return (
                  <Col key={itemId} xs={6} sm={6} md={4} lg={3}>
                    <motion.div
                      className="menu-item-card"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => open && handleOpenModal(item)}
                      style={{ opacity: open ? 1 : 0.7, pointerEvents: open ? 'auto' : 'none' }}
                    >
                      {/* IMAGE AT TOP, FULL WIDTH */}
                      <img
                        src={item.image ? item.image : 'https://via.placeholder.com/150'}
                        alt={item.name}
                        className="menu-item-img"
                      />

                      {/* CONTENT BELOW */}
                      <div className="menu-item-info">
                        <h3 className="menu-item-name">{item.name}</h3>
                        
                        <div className="menu-item-footer">
                          <span className="menu-item-price">
                            {formatPrice(item.price, currency, currencySymbol)}
                          </span>

                          {qty === 0 ? (
                            <button
                              className="add-to-cart-btn"
                              disabled={!open}
                              onClick={(e) => { e.stopPropagation(); if (open) handleItemAdd({ ...item, quantity: 1 }); }}
                            >
                              Add to cart
                            </button>
                          ) : (
                            <div className="qty-controls" onClick={(e) => e.stopPropagation()}>
                              <button className="qty-btn" onClick={() => { if (qty > 1) updateItemQuantity(cartItem._id, qty - 1); else updateItemQuantity(cartItem._id, 0); }}>−</button>
                              <span className="qty-value">{qty}</span>
                              <button className="qty-btn" onClick={() => updateItemQuantity(cartItem._id, qty + 1)}>+</button>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <p className="no-menu-items">No menu items available in this category.</p>
          )}
        </div>
      </Container>

      {selectedItem && (
        <ItemModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          item={selectedItem}
          handleAddToCart={handleItemAdd}
          cart={cart}
          vendor_id={restaurant._id}
          vendor_type="restaurant"
          currency={currency}
          currencySymbol={currencySymbol}
        />
      )}
    </motion.div>
  );
};

export default RestaurantDetail;